import * as React from 'react';
import { ECaptionsItemType, IPlayerListener, ITargetCaptionsProps } from './types';
import { theme } from '../../../../../common/styles/theme';
import { Box, CircularProgress, Stack } from '@mui/material';
import { TargetCaptionItem } from './TargetCaptionItem';
import { ICaptionsItem } from '../../../../types/common';
import { DeviceContext } from '../../../../../common/contexts/DeviceContext';
import { ElementHeightCalc } from '../../../../../common/utils/elementHeightCalc';
import { Text } from '../../../../../common/components/Text/Text';
import { TextUtils } from '../../../../../common/utils/text-utils';

export let targetItemsHeightList: number[] = [];

const getSpeakerKey = (caption: ICaptionsItem): string => {
  if (!caption?.userName) return '';
  return caption.userId ? `id:${caption.userId}` : `name:${caption.userName}`;
}

type TRangeInfo = {
  startIndex: number,
  endIndex: number
}

enum ERenderElementType {ITEMS, BLOCK};
type TRenderElement = {
  startIndex: number,
  endIndex: number,
  type: ERenderElementType.ITEMS
}
type TRenderBlock = {
  height: number,
  type: ERenderElementType.BLOCK
}
type TRenderItem = TRenderElement | TRenderBlock;

export const TargetCaptions: React.FC<ITargetCaptionsProps> = ({
  elementRef,
  captions,
  videoId,
  langCode,
  groupLangName,
  solo,
  playerActive,
  forwardCaptionIndex,
  listenerHolder,
  showTranslations,
  playerExist,
  loadCaptionsError,
  onCaptionPlay,
  onCaptionDblClick,
  onMouseEnter,
  onMouseLeave,
  onChangeActiveIndex,
  onCaptionMouseEnter,
  onCaptionMouseLeave,
  onCheckLoadCaptions
}) => {
  const {isFullScreen, isMobile} = React.useContext(DeviceContext);

  const targetCaptionsContainer = elementRef!; // the only caller always passes it
  const [targetScrollTop, setTargetScrollTop] = React.useState<number>(-1);
  const [scrolling, setScrolling] = React.useState<boolean>(false);
  const [activeCaptionIndex, setActiveCaptionIndex] = React.useState<number>(0);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [captionHeightsCalc, setCaptionHeightsCalc] = React.useState<boolean>(false);
  const calcHeightElmRef = React.useRef<any>(null);
  const [startedPlayIndex, setStartedPlayIndex] = React.useState<number>(-1);
  // Speakers are numbered by first appearance in the WHOLE caption set, not in the rendered window,
  // and recomputed whenever the set changes — scrolling or a captions reload can't shuffle the colors.
  // Captions the backend couldn't resolve to a user (userId 0) are grouped by name instead.
  const speakers = React.useMemo(() => {
    const order: string[] = [];
    const initials: Record<string, string> = {};
    (captions || []).forEach(caption => {
      const key = getSpeakerKey(caption);
      if (!key || initials[key] !== undefined) return;
      order.push(key);
      initials[key] = TextUtils.getInitials(caption.userName!);
    });
    return {order, initials};
  }, [captions]);

  const getSpeaker = (caption: ICaptionsItem): {index: number, initials: string} | null => {
    const key = getSpeakerKey(caption);
    if (!key) return null;
    return {index: speakers.order.indexOf(key), initials: speakers.initials[key]};
  };

  const captionsRef = React.useRef<ICaptionsItem[]>(captions);
  captionsRef.current = captions;

  const handleMouseLeave = () => {
    onMouseLeave();
  };
  const handleMouseEnter = () => {
    onMouseEnter();
  };

  const captionPlayPauseClickHandler = (index: number) => {
    const caption: ICaptionsItem = captionsRef.current[index];
    onCaptionPlay(caption, playerActive);
    setStartedPlayIndex(index);
    setTimeout(() => {
      setStartedPlayIndex(-1);
    })
    onTargetIndexChange(index);
  };

  const captionDblClickHandler = (index: number) => {
    const caption: ICaptionsItem = captionsRef.current[index];
    if (caption) {
      onCaptionDblClick(index, caption);
      onTargetIndexChange(index);
    }
  }

  const onTargetIndexChange = (index: number) => {
    setActiveCaptionIndex(index);
    onChangeActiveIndex(index);
  };

  const playerListener: IPlayerListener = {
    onTargetIndexChange,
  }

  React.useEffect(() => {
    listenerHolder.addListener(playerListener);
    let timer = setInterval(async () => {
      if (onCheckLoadCaptions) {
        await onCheckLoadCaptions(); // на случай если не установлен икстеншен и не загружены капшены, отслеживает включение икстеншена и подгружает капшены
      }
    }, 5000);
    return () => {
      clearInterval(timer);
      listenerHolder.removeListener(playerListener);
    };
  }, [videoId, langCode]);

  React.useEffect(() => {
    if (captions && captions.length > 0) {
      const defaultHeight = captions.length > 500 ? 18 : 0; // do not allow load all target caption items for big videos
      targetItemsHeightList = Array(captions.length).fill(defaultHeight);
      setLoading(false);
      if (!captionHeightsCalc) {
        setTimeout(() => {
          calcCaptionItemsHeight();
          setCaptionHeightsCalc(true);
        })
      }
    } else {
      targetItemsHeightList = [];
      setLoading(true);
    }

  }, [captions]);

  const calcCaptionItemsHeight = () => {
    if (!calcHeightElmRef.current || !targetCaptionsContainer.current) return;

    const len = captions.length
    const {height} = calcHeightElmRef.current.getBoundingClientRect();
    let width = targetCaptionsContainer.current.getBoundingClientRect().width;
    const items = document.getElementsByClassName('caption-item');
    if (items && items.length) {
      width = items[0].getBoundingClientRect().width
    }

    const lineHeight = Math.round(height);
    const fontSize = 18;
    const fontFamily = '"Twemoji Country Flags", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif';
    const fontWeight = '400';
    const heightCalc = new ElementHeightCalc({
      lineHeight, width, fontWeight, fontSize, fontFamily
    })

    for (let i = 0; i < len; i++) {
      const height: number = heightCalc.getHeight(captions[i].text);
      targetItemsHeightList[i] = height;
    }
  }

  React.useEffect(() => {
    if (forwardCaptionIndex < 0) return;
    scrollToIndex(forwardCaptionIndex);
  }, [forwardCaptionIndex]);

  const [fullScreenHeight, setFullScreenHeight] = React.useState(0);
  React.useEffect(() => {
    if (isFullScreen) {
      let startIndex = 0;
      let endIndex;
      if (!activeCaptionIndex) {
        endIndex = 2;
      } else {
        startIndex = activeCaptionIndex - 1;
        endIndex = activeCaptionIndex + 1;
      }

      let height = 0;
      for (let i = startIndex; i <= endIndex; i++) {
        height += targetItemsHeightList[i];
      }
      setFullScreenHeight(height - 2);
      setTimeout(() => {
        targetCaptionsContainer && targetCaptionsContainer.current?.scrollTo({
          top: 1000,
          behavior: 'smooth',
        })
      }, 100);
      return;
    } else {
      setFullScreenHeight(0);
    }

    const parentTop = targetCaptionsContainer && targetCaptionsContainer.current?.scrollTop as number;
    const parentBottom = parentTop + (targetCaptionsContainer && targetCaptionsContainer.current?.offsetHeight as number);
    const captionTop = getCaptionTopPos(activeCaptionIndex);
    const captionBottom = captionTop + targetItemsHeightList[activeCaptionIndex];

    if (activeCaptionIndex < 0) return;
    if (parentBottom - captionBottom <= 100 ||
      parentTop - captionTop > 0
    ) {
      scrollToIndex(activeCaptionIndex);
    }
  }, [activeCaptionIndex]);

  const scrollToIndex = (index: number) => {
    const scrollValue = getCaptionTopPos(index);
    targetCaptionsContainer && targetCaptionsContainer.current?.scrollTo({
      top: scrollValue,
      behavior: 'smooth',
    })
  }

  const getCaptionTopPos = (index: number) => {
    let result = 0;
    for(let i = 0; i < index; i++) {
      if (i < targetItemsHeightList.length) {
        result += targetItemsHeightList[i];
      }
    }
    return result;
  }

  // Re-render (at most once per frame) when a measured height or the container size changes —
  // otherwise the spacer heights stay on the initial estimates and the list's end is never reached.
  const [, setLayoutVersion] = React.useState(0);
  const relayoutScheduledRef = React.useRef(false);
  const scheduleRelayout = () => {
    if (relayoutScheduledRef.current) return;
    relayoutScheduledRef.current = true;
    requestAnimationFrame(() => {
      relayoutScheduledRef.current = false;
      setLayoutVersion(v => v + 1);
    });
  };

  React.useEffect(() => {
    const container = targetCaptionsContainer?.current;
    if (!container) return;
    const observer = new ResizeObserver(scheduleRelayout);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const onItemSetHeight = (index: number, height: number) => {
    if (targetItemsHeightList[index] === height) return;
    targetItemsHeightList[index] = height;
    scheduleRelayout();
  }

  const getVisibleItemsIndexRange = () => {
    let firstItemIndex = -1;
    let lastItemIndex = -1;
    let currentTop = 0;
    const scrollHeight = targetCaptionsContainer && targetCaptionsContainer.current ? targetCaptionsContainer.current.offsetHeight : 0;
    const scrollBottom = targetScrollTop + scrollHeight;
    for(let i=0; i<captions.length; i++) {
      currentTop += targetItemsHeightList[i];
      if (firstItemIndex < 0 && currentTop >= targetScrollTop) {
        firstItemIndex = i - 1;
      }
      if (lastItemIndex < 0 && currentTop >= scrollBottom) {
        lastItemIndex = i + 1;
      }
    }
    if (firstItemIndex < 0)
      firstItemIndex = 0;
    if (lastItemIndex < 0)
      lastItemIndex = captions.length-1;

    return {firstItemIndex, lastItemIndex};
  }

  const getTargetCaptionItems = (firstItemIndex: number, lastItemIndex: number) => {
    const list: JSX.Element[] = [];
    for(let index = firstItemIndex; index <= lastItemIndex; index++) {
      const caption = captions[index];
      if (!caption) continue;
      const isActive = index === activeCaptionIndex;

      const speaker = getSpeaker(caption);

      list.push(
      <TargetCaptionItem
        index={index}
        captionText={caption.text}
        startTime={caption.startTime}
        endTime={caption.endTime}
        isActive={isActive}
        type={ECaptionsItemType.TARGET}
        showPlayPause={playerExist}
        onPlayPauseClick={captionPlayPauseClickHandler}
        onItemDblClick={captionDblClickHandler}
        onMouseEnter={onCaptionMouseEnter}
        onMouseLeave={onCaptionMouseLeave}
        key={`caption-${index}-${caption.startTime}-${caption.endTime}`}
        playerActive={playerActive}
        onSetHeight={onItemSetHeight}
        showTranslations={showTranslations}
        userName={speaker?.initials}
        userColorIndex={speaker?.index}
      />)
    }
    return list;
  }

  const getItemsRangeList = (): TRangeInfo[] => {
    const {firstItemIndex, lastItemIndex} = getVisibleItemsIndexRange()
    return [{
      startIndex: firstItemIndex,
      endIndex: lastItemIndex
    }];
  }

  const getRenderElements = (): TRenderItem[] => {
    if (isFullScreen) {
      let startIndex = 0;
      let endIndex;
      if (!activeCaptionIndex) {
        endIndex = 2;
      } else {
        let isFirst = activeCaptionIndex === 1;
        startIndex = activeCaptionIndex - 1 - +!isFirst;
        endIndex = activeCaptionIndex + 1 + +isFirst;
      }

      return [{
        type: ERenderElementType.ITEMS,
        startIndex,
        endIndex,
      }];
    }

    const rangeList: TRangeInfo[] = getItemsRangeList();

    let topSpaceHeight = 0;
    for(let i=0; i<rangeList[0].startIndex; i++) {
      topSpaceHeight += targetItemsHeightList[i];
    }
    let bottomSpaceHeight = 0;
    for(let i=rangeList[rangeList.length-1].endIndex+1; i<captions.length; i++) {
      bottomSpaceHeight += targetItemsHeightList[i];
    }
    const result: TRenderItem[] = [];
    if (topSpaceHeight > 0) {
      result.push({
        type: ERenderElementType.BLOCK,
        height: topSpaceHeight
      })
    }

    result.push({
      type: ERenderElementType.ITEMS,
      startIndex: rangeList[0].startIndex,
      endIndex: rangeList[0].endIndex
    });

    if (bottomSpaceHeight > 0) {
      result.push({
        type: ERenderElementType.BLOCK,
        height: bottomSpaceHeight
      })
    }

    return result;
  }

  const items = getRenderElements()
    .map((elm: TRenderItem, index) => {
      if (elm.type === ERenderElementType.BLOCK) {
        return (<div	style={{ minHeight: elm.height+'px' }} key={`element-${index}`}></div>)
      }
      if (elm.type === ERenderElementType.ITEMS) {
        return getTargetCaptionItems(elm.startIndex, elm.endIndex);
      }
    });

  const onScroll = () => {
    setScrolling(true);
    if (targetCaptionsContainer && targetCaptionsContainer.current) {
      setTargetScrollTop(targetCaptionsContainer.current.scrollTop);
    }
  }

  return (
    <Box
      className={`captions-parent`}
      sx={{
        position: 'relative',
        overflowY: 'auto',
        minHeight: 0,
        width: solo ? '100%' : '50%',
        height: isFullScreen && fullScreenHeight ? `${fullScreenHeight}px` : '100%',
        flex: 1,
        pl: theme.spacing(4.5),
        pt: theme.spacing(4),

        '.root-inner.fullscreen &': {
			    overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          p: 0,
				},

        '.mobile & *': {
          pointerEvents: 'none',
        },
      }}
      data-type={ECaptionsItemType.TARGET}
      ref={elementRef}
      onScroll={onScroll}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >

      <Stack alignItems={'center'} justifyContent={'center'}>
        {loadCaptionsError &&
        <Text
          variant={'subtitle2'}
          sx={{
            fontWeight: 600,
          }}
        >
          Unable load captions for {groupLangName}
        </Text>
        }
        {!loadCaptionsError && !captions?.length &&
        <CircularProgress/>
        }
      </Stack>

      {!loading && items}

     {/* used for calulate element bound*/}
      <Box ref={calcHeightElmRef} style={{
        position: 'absolute',
        visibility: 'hidden',
        lineHeight: '20px',
        fontSize: '14px',
        fontFamily: '"Twemoji Country Flags", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
        fontWeight: '400',
        verticalAlign: 'text-bottom'
      }}>hp</Box>

    </Box>

  )
};
